/**
 * Bestyrelsens sider: roller og forhåndsadgang, statistik og
 * Conventus-konfiguration.
 *
 * Statistikken er formuleret ærligt: den viser registrerede sidevisninger,
 * ikke sessions, og forklarer definitionerne direkte på siden (§13).
 */

import { useState } from 'react';
import {
  antal,
  api,
  departmentLabel,
  formatDateTime,
} from '../lib/api.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  EmptyState,
  Field,
  Meta,
  Notice,
  PageHead,
  SectionHead,
  useAction,
  useAsync,
} from '../components/ui.js';
import { Icon } from '../components/icons.js';
import { DEPARTMENTS, DEPARTMENT_LABELS, roleLabel } from '../../shared/domain.js';



/* ------------------------------------------------------------------ */
/* Roller og adgang                                                    */
/* ------------------------------------------------------------------ */

interface AccessUser {
  id: string;
  name: string;
  email: string;
  role: string;
  departments: string[];
  trainerTeamIds: string[];
  hasFamilyLinks: boolean;
}

interface AccessData {
  users: AccessUser[];
  presets: Array<{
    id: string;
    email: string;
    role: string;
    department: string | null;
    teamIds: string[];
    consumedAt: string | null;
  }>;
  teams: Array<{ teamId: string; name: string; department: string }>;
  roleNote: string;
}

/**
 * De roller, den aktuelle bruger må tildele.
 *
 * Samme regel som på serveren — den her er kun til for at vise den. Serveren
 * afviser uanset hvad formularen tilbyder; det er derfor listen ikke er
 * hentet fra et API-svar, men skrevet et sted, hvor den er let at finde.
 */
function assignableRoles(role: string): string[] {
  if (role === 'admin') return ['member', 'trainer', 'committee', 'board', 'admin'];
  if (role === 'board') return ['member', 'trainer'];
  return [];
}

export function RolesPage({
  currentUserId,
  role: actorRole,
}: {
  currentUserId: string;
  role: string;
}): JSX.Element {
  const state = useAsync<AccessData>(() => api.get('/api/user-access'));
  const [editing, setEditing] = useState<string | null>(null);

  return (
    <div className="stack">
      <PageHead
        title="Roller og adgang"
        eyebrow={actorRole === 'admin' ? 'Administrator' : 'Bestyrelse'}
        eyebrowIcon="roles"
      />
      <AsyncSection state={state} skeletonLines={6}>
        {(data) => (
          <>
            <Notice kind="info">{data.roleNote}</Notice>
            {actorRole !== 'admin' ? (
              <Notice kind="warning">
                <strong>Du kan tildele instruktør og medlem.</strong>
                <p className="small mt-1">
                  Bestyrelses-, udvalgs- og administratorrollen tildeles af foreningens
                  administrator. De tre er dem, der giver adgang til at give adgang.
                </p>
              </Notice>
            ) : null}

            <section className="card">
              <h2>Registrerede konti</h2>
              <div className="stack stack--tight mt-3">
                {data.users.map((user) => (
                  <div key={user.id}>
                    <div className="row row--between">
                      <div>
                        <div className="small">
                          <strong>{user.name}</strong>
                          {user.id === currentUserId ? <span className="muted"> (dig)</span> : null}
                        </div>
                        <div className="tiny muted">{user.email}</div>
                      </div>
                      <div className="row" style={{ gap: 6 }}>
                        <Badge>{roleLabel(user.role)}</Badge>
                        {user.hasFamilyLinks ? <Badge kind="ok">Familie</Badge> : null}
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          onClick={() => setEditing(editing === user.id ? null : user.id)}
                          aria-expanded={editing === user.id}
                        >
                          {editing === user.id ? 'Luk' : 'Ret'}
                        </button>
                      </div>
                    </div>
                    {editing === user.id ? (
                      <RoleEditor
                        user={user}
                        teams={data.teams}
                        actorRole={actorRole}
                        isSelf={user.id === currentUserId}
                        onSaved={() => {
                          setEditing(null);
                          state.reload();
                        }}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

            <PresetSection
              presets={data.presets}
              teams={data.teams}
              actorRole={actorRole}
              onChanged={state.reload}
            />
          </>
        )}
      </AsyncSection>
    </div>
  );
}

function RoleEditor({
  user,
  teams,
  actorRole,
  isSelf,
  onSaved,
}: {
  user: AccessUser;
  teams: AccessData['teams'];
  actorRole: string;
  isSelf: boolean;
  onSaved: () => void;
}): JSX.Element {
  const allowed = assignableRoles(actorRole);
  // Kontoens NUVÆRENDE rolle skal stå i listen, også når den aktuelle
  // bruger ikke må tildele den — ellers ville formularen se ud, som om
  // rollen var noget andet, end den er.
  const options = allowed.includes(user.role) ? allowed : [user.role, ...allowed];
  const locked = !allowed.includes(user.role);
  const [role, setRole] = useState(user.role);
  const [department, setDepartment] = useState(user.departments[0] ?? '');
  const [teamIds, setTeamIds] = useState<string[]>(user.trainerTeamIds);
  const action = useAction();

  const save = (): void => {
    void action.run(async () => {
      await api.put(`/api/user-access/${user.id}`, {
        role,
        departments: role === 'committee' && department ? [department] : [],
        trainerTeamIds: role === 'trainer' ? teamIds : [],
      });
      onSaved();
    });
  };

  return (
    <div className="card stack stack--tight" style={{ marginTop: 10, background: 'var(--bg)' }}>
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {isSelf ? (
        <Notice kind="warning">
          Du kan ikke sætte din egen rolle ned her. Bed en anden med mindst samme adgang om at
          ændre den.
        </Notice>
      ) : null}
      {locked ? (
        <Notice kind="warning">
          Kontoen har højere adgang, end du kan tildele. Kun foreningens administrator kan ændre
          den.
        </Notice>
      ) : null}

      <Field label="Primær rolle" htmlFor={`role-${user.id}`}>
        <select
          id={`role-${user.id}`}
          value={role}
          disabled={locked}
          onChange={(e) => setRole((e.target as HTMLSelectElement).value)}
        >
          {options.map((value) => (
            <option key={value} value={value}>
              {roleLabel(value)}
            </option>
          ))}
        </select>
      </Field>

      {role === 'committee' ? (
        <Field
          label="Afdeling"
          htmlFor={`dept-${user.id}`}
          hint="Udvalgsadgang tildeles manuelt og udledes aldrig fra Conventus."
        >
          <select
            id={`dept-${user.id}`}
            value={department}
            onChange={(e) => setDepartment((e.target as HTMLSelectElement).value)}
          >
            <option value="">Vælg afdeling</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {DEPARTMENT_LABELS[d]}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      {role === 'trainer' ? (
        <div className="field">
          <span style={{ fontSize: 14, fontWeight: 600 }}>Instruktørhold</span>
          <span className="field__hint">
            Vælg de hold, kontoen skal kunne administrere. Holdene kan ligge i forskellige
            afdelinger.
          </span>
          <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6 }}>
            {teams.map((team) => (
              <label key={team.teamId} className="checkbox">
                <input
                  type="checkbox"
                  checked={teamIds.includes(team.teamId)}
                  onChange={(e) =>
                    setTeamIds((prev) =>
                      (e.target as HTMLInputElement).checked
                        ? [...prev, team.teamId]
                        : prev.filter((id) => id !== team.teamId),
                    )
                  }
                />
                <span className="small">
                  {team.name}
                  <span className="muted"> · {departmentLabel(team.department)}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <ActionButton pending={action.pending} onClick={save}>
        Gem adgang
      </ActionButton>
    </div>
  );
}

function PresetSection({
  presets,
  teams,
  actorRole,
  onChanged,
}: {
  presets: AccessData['presets'];
  teams: AccessData['teams'];
  actorRole: string;
  onChanged: () => void;
}): JSX.Element {
  // Forhåndsadgang er den samme tildeling, bare på forskud — og derfor
  // den samme grænse. Uden den kunne et bestyrelsesmedlem oprette en
  // administrator ved at skrive en mailadresse ind, der endnu ikke har
  // en konto.
  const allowed = assignableRoles(actorRole);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [department, setDepartment] = useState('');
  const action = useAction();
  const removing = useAction();

  /*
    Der spørges «er du sikker» med adressen i teksten og ikke bare «er du
    sikker». Det er den eneste måde at opdage, at man er ved at fjerne den
    forkerte linje i en liste, hvor alle linjer ligner hinanden.
  */
  const remove = (id: string, adresse: string): void => {
    void removing.run(async () => {
      await api.del(`/api/user-access/presets/${encodeURIComponent(id)}`);
      removing.setSuccess(`Forhåndsadgangen til ${adresse} er fjernet.`);
      onChanged();
    });
  };

  const create = (): void => {
    void action.run(async () => {
      await api.post('/api/user-access/presets', {
        email,
        role,
        department: role === 'committee' && department ? department : undefined,
        teamIds: [],
        note: '',
      });
      action.setSuccess('Forhåndsadgangen er gemt. Der er ikke sendt en mail.');
      setEmail('');
      onChanged();
    });
  };

  void teams;

  return (
    <section className="card">
      <h2>Forhåndsadgang</h2>
      <p className="small muted mt-2">
        Giv en rolle til en mailadresse, før personen logger ind første gang. Det er{' '}
        <strong>ikke en mailinvitation</strong> — der sendes ingen besked. Er mailen allerede
        registreret, skal adgangen rettes ovenfor i stedet.
      </p>

      <div className="stack stack--tight mt-3">
        {action.error ? <Notice kind="error">{action.error}</Notice> : null}
        {action.success ? <Notice kind="success">{action.success}</Notice> : null}
        <Field label="Mailadresse" htmlFor="preset-email" error={action.fieldError('email')}>
          <input
            id="preset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
        </Field>
        <Field label="Rolle" htmlFor="preset-role">
          <select
            id="preset-role"
            value={role}
            onChange={(e) => setRole((e.target as HTMLSelectElement).value)}
          >
            {allowed.map((value) => (
              <option key={value} value={value}>
                {roleLabel(value)}
              </option>
            ))}
          </select>
        </Field>
        {role === 'committee' ? (
          <Field label="Afdeling" htmlFor="preset-dept">
            <select
              id="preset-dept"
              value={department}
              onChange={(e) => setDepartment((e.target as HTMLSelectElement).value)}
            >
              <option value="">Vælg afdeling</option>
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {DEPARTMENT_LABELS[d]}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <ActionButton pending={action.pending} onClick={create} disabled={!email.trim()}>
          Gem forhåndsadgang
        </ActionButton>
      </div>

      {removing.error ? (
        <div className="mt-3">
          <Notice kind="error">{removing.error}</Notice>
        </div>
      ) : null}
      {removing.success ? (
        <div className="mt-3">
          <Notice kind="success">{removing.success}</Notice>
        </div>
      ) : null}

      {presets.length > 0 ? (
        <ul className="list mt-4">
          {presets.map((preset) => (
            <li key={preset.id} className="row row--between">
              <span className="small">{preset.email}</span>
              <span className="row" style={{ gap: 6 }}>
                <Badge>{roleLabel(preset.role)}</Badge>
                {preset.consumedAt ? <Badge kind="ok">Brugt</Badge> : <Badge>Afventer</Badge>}
                {/*
                  EN FORHÅNDSADGANG SKAL KUNNE FORTRYDES.
                  Serveren har altid kunnet fjerne en, men der var ingen
                  knap. En stavefejl i en mailadresse — eller en rolle,
                  der viste sig at være for høj — kunne dermed ikke
                  trækkes tilbage af den, der lavede den. Og en adresse
                  med en rolle på forskud er ikke uskyldig: den, der en
                  dag logger ind på præcis den adresse, arver rollen.

                  Kun en ubrugt kan fjernes. Er den brugt, findes der en
                  rigtig konto, og den rettes i listen ovenfor — at slette
                  forhåndsadgangen ville ikke tage adgangen fra nogen, og
                  det ville se ud som om den gjorde.
                */}
                {preset.consumedAt ? null : (
                  <ActionButton
                    className="btn btn--ghost btn--small"
                    pending={removing.pending}
                    onClick={() => remove(preset.id, preset.email)}
                  >
                    Fjern
                  </ActionButton>
                )}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Statistik                                                           */
/* ------------------------------------------------------------------ */

interface Dashboard {
  activeAccounts7d: number;
  activeAccounts30d: number;
  pageViews30d: number;
  registeredAccounts: number;
  accountsWithFamily: number;
  activationRate: number | null;
  activationRateDefinition: string;
  trend: Array<{ weekStart: string; pageViews: number; activeAccounts: number }>;
  topPages: Array<{ path: string; pageViews: number; activeAccounts: number }>;
  departments: Array<{
    department: string;
    label: string;
    pageViews: number;
    activeAccounts: number;
    linkedAccounts: number;
    activationRate: number | null;
  }>;
  counts: {
    readReceipts: number;
    absences: number;
    attendanceSessions: number;
    volunteerSignups: number;
    activePushUsers: number;
  };
  observations: Array<{ level: string; message: string }>;
  definitions: string[];
}

/**
 * Brug af appen — bevidst kort.
 *
 * HVORFOR DEN ER SKÅRET NED
 * Den oprindelige side viste sidevisninger pr. side, pr. afdeling, læste
 * beskeder, gemte fremmøder og otte tal mere. Ingen i en frivillig bestyrelse
 * træffer en beslutning ud fra «/kalender havde 412 visninger».
 *
 * Der er kun tre spørgsmål, siden skal kunne svare på:
 *   1. Er familierne kommet i gang? (tilknytning)
 *   2. Bruger de den løbende? (otte ugers udvikling)
 *   3. Når vores beskeder frem? (push)
 *
 * Resten regnes stadig i API'et og kan hentes af den, der vil grave — det er
 * bare ikke det, siden handler om.
 */
export function StatisticsPage(): JSX.Element {
  const state = useAsync<{ dashboard: Dashboard }>(() => api.get('/api/workspace/statistics'));

  return (
    <div className="stack">
      <PageHead
        title="Brug af appen"
        eyebrow="Statistik"
        eyebrowIcon="stats"
        sub="Tal om brugen — aldrig om den enkelte"
      />
      <AsyncSection state={state} skeletonLines={8}>
        {({ dashboard: d }) => {
          const maxViews = Math.max(1, ...d.trend.map((t) => t.pageViews));
          return (
            <>
              {d.observations.map((o, i) => (
                <Notice key={i} kind={o.level === 'warning' ? 'warning' : 'info'}>
                  {o.message}
                </Notice>
              ))}

              <section className="card">
                <h2>Er familierne kommet i gang?</h2>
                <div className="card-grid card-grid--2 mt-3">
                  <div className="stat">
                    <div className="stat__value">{d.registeredAccounts}</div>
                    <div className="stat__label">Konti oprettet</div>
                  </div>
                  <div className="stat">
                    <div className="stat__value">{d.accountsWithFamily}</div>
                    <div className="stat__label">Har forbundet familien</div>
                  </div>
                </div>
                <p className="tiny muted mt-3">
                  En konto uden familietilknytning ser hverken hold eller kalender. Er forskellen
                  mellem de to tal stor, er det dér, indsatsen skal lægges.
                </p>
              </section>

              <section className="card">
                <h2>Bruger de den løbende?</h2>
                <div className="card-grid card-grid--2 mt-3">
                  <div className="stat">
                    <div className="stat__value">{d.activeAccounts7d}</div>
                    <div className="stat__label">Aktive den seneste uge</div>
                  </div>
                  <div className="stat">
                    <div className="stat__value">{d.activeAccounts30d}</div>
                    <div className="stat__label">Aktive den seneste måned</div>
                  </div>
                </div>

                {d.pageViews30d === 0 ? (
                  <div className="mt-3">
                    <EmptyState title="Ingen registreret aktivitet endnu">
                      Der er ikke registreret brug i perioden. Grafen fyldes ikke med opdigtede tal.
                    </EmptyState>
                  </div>
                ) : (
                  <div className="mt-4">
                    <h3 className="small">Otte uger</h3>
                    <div className="bars" role="img" aria-label="Aktive konti pr. uge">
                      {d.trend.map((point) => (
                        <div key={point.weekStart} className="bars__col">
                          <div
                            className="bars__bar"
                            style={{ height: `${(point.pageViews / maxViews) * 100}%` }}
                            title={`${point.activeAccounts} konti brugte appen i ugen fra ${point.weekStart}`}
                          />
                          <span className="bars__label">{point.weekStart.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                    <table className="table-cards mt-3">
                      <caption className="sr-only">Aktive konti pr. uge</caption>
                      <thead>
                        <tr>
                          <th scope="col">Uge fra</th>
                          <th scope="col">Aktive konti</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.trend.map((point) => (
                          <tr key={point.weekStart}>
                            <td data-label="Uge fra">{point.weekStart}</td>
                            <td data-label="Aktive konti">{point.activeAccounts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="card">
                <h2>Når beskederne frem?</h2>
                <div className="card-grid card-grid--2 mt-3">
                  <div className="stat">
                    <div className="stat__value">{d.counts.activePushUsers}</div>
                    <div className="stat__label">Har slået notifikationer til</div>
                  </div>
                  <div className="stat">
                    <div className="stat__value">
                      {d.registeredAccounts === 0
                        ? '—'
                        : `${Math.round((d.counts.activePushUsers / d.registeredAccounts) * 100)} %`}
                    </div>
                    <div className="stat__label">Af alle konti</div>
                  </div>
                </div>
                <p className="tiny muted mt-3">
                  En aflysning når kun frem til dem, der har slået notifikationer til. Alle andre
                  skal selv åbne appen — derfor står aflysningen også altid i kalenderen.
                </p>
              </section>

              <section className="card">
                <h2>Sådan skal tallene læses</h2>
                <ul style={{ margin: '10px 0 0 18px', padding: 0 }}>
                  {d.definitions.map((line, i) => (
                    <li key={i} className="small muted" style={{ marginBottom: 6 }}>
                      {line}
                    </li>
                  ))}
                </ul>
                <p className="tiny muted mt-3">
                  Tallene er tællinger af faktiske hændelser i appen. Der er ingen skøn, ingen
                  fremskrivning og ingen sammenligning med andre foreninger.
                </p>
              </section>
            </>
          );
        }}
      </AsyncSection>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Conventus                                                           */
/* ------------------------------------------------------------------ */

interface BulkResult {
  results: Array<{ groupId: string; name: string; status: string; memberCount: number; message: string }>;
  ok: number;
  blocked: number;
  failed: number;
  message: string;
}

/** Ét undersøgelseskald. Kun struktur — se `probeGroupListing` på serveren. */
interface ProbeAttempt {
  label: string;
  url: string;
  status: number | null;
  bytes: number;
  groupCount: number;
  shape: string;
  fields: string[];
  trainingTimesShape?: string;
  remoteError: string | null;
  failure: string | null;
}

interface ProbeResult {
  attempts: ProbeAttempt[];
  samples: Array<{ groupId: string; title: string; trainingTimes: string }>;
  fill: Array<{ field: string; filled: number; hasChildren: boolean }>;
  withTimes: number;
  total: number;
}

interface ConventusConfig {
  settings: {
    organisationId: string;
    testGroupId: string;
    hasApiKey: boolean;
    apiKeyUpdatedAt: string | null;
    lastTestAt: string | null;
    lastTestStatus: string | null;
    lastTestMessage: string;
  } | null;
  directory: { status: string; lastSuccessAt: string | null; memberCount: number; message: string } | null;
  organisationCount: number;
  organisations: string[];
  autoSync: {
    enabled: boolean;
    intervalMinutes: number;
    directoryHours: number;
    teams: AutoSyncRun | null;
    directory: AutoSyncRun | null;
  };
  catalogSize: number;
  probePathCount: number;
  endpointNote: string;
}

interface AutoSyncRun {
  at: string;
  teams: { ok: number; blocked: number; failed: number; message: string } | null;
  directory: { members: number; message: string } | null;
  error: string | null;
}

/* ------------------------------------------------------------------ */
/* Krypteringsnøglen                                                   */
/* ------------------------------------------------------------------ */

interface NoegleStatus {
  organisations: Array<{
    organisationId: string;
    status: 'current' | 'previous' | 'unreadable' | 'none';
    updatedAt: string | null;
  }>;
  hasPreviousKey: boolean;
}

/**
 * SKIFT AF KRYPTERINGSNØGLEN — UDEN AT NOGEN SKAL FINDE API-NØGLEN FREM.
 *
 * Nøglen, der krypterer foreningens Conventus-API-nøgle, har stået i en
 * chatsamtale og skal skiftes. Den almindelige vej ville være at hente
 * API-nøglen i Conventus og skrive den ind igen — og så ligger foreningens
 * API-nøgle i en mail eller på en seddel. Serveren kan gøre det selv, mens
 * den har begge krypteringsnøgler: den læser med den gamle og skriver med
 * den nye. Den rå API-nøgle kommer aldrig i nærheden af en browser.
 *
 * Kortet står kun frem, når der ER noget at gøre eller noget at vide.
 * Ellers er det endnu en knap på en side, der har rigeligt.
 */
function KrypteringsnoegleCard(): JSX.Element | null {
  const status = useAsync<NoegleStatus>(() => api.get('/api/conventus/noegle'));
  const action = useAction();
  const [bekraefter, setBekraefter] = useState(false);
  const [nyNoegle, setNyNoegle] = useState<string | null>(null);
  const [kopieret, setKopieret] = useState(false);
  const [aabn, setAabn] = useState(false);
  const [fejl, setFejl] = useState<string | null>(null);

  const data = status.data;
  if (!data) return null;

  const gamle = data.organisations.filter((o) => o.status === 'previous');
  const tabte = data.organisations.filter((o) => o.status === 'unreadable');
  const igang = gamle.length > 0;

  /*
    Nøglen laves i BROWSEREN, ligesom pushnøglerne. Den er 32 tilfældige
    bytes; den skal ingen steder hen andet end ind i Railway, og den må
    aldrig gå gennem en chat eller en mail — det er jo netop derfor, den
    nuværende skal skiftes.
  */
  const generer = (): void => {
    setFejl(null);
    try {
      const raa = crypto.getRandomValues(new Uint8Array(32));
      let binaer = '';
      for (const b of raa) binaer += String.fromCharCode(b);
      setNyNoegle(btoa(binaer));
    } catch {
      setFejl('Browseren kunne ikke lave en nøgle. Prøv en anden browser.');
    }
  };

  const kopier = (): void => {
    if (!nyNoegle) return;
    void navigator.clipboard
      .writeText(nyNoegle)
      .then(() => {
        setKopieret(true);
        setTimeout(() => setKopieret(false), 2000);
      })
      .catch(() => {
        // Udklipsholderen kan være spærret. Værdien står på skærmen.
        setFejl('Kunne ikke kopiere automatisk. Markér nøglen og kopiér den i hånden.');
      });
  };

  const omkrypter = (): void => {
    void action.run(async () => {
      const r = await api.post<{ reencrypted: number; alreadyCurrent: number; unreadable: number }>(
        '/api/conventus/noegle/omkrypter',
      );
      action.setSuccess(
        r.reencrypted > 0
          ? `${antal(r.reencrypted, 'organisation er', 'organisationer er')} krypteret om med den nye nøgle. ` +
              'Nu kan CONVENTUS_CREDENTIAL_ENCRYPTION_KEY_PREVIOUS fjernes fra Railway.'
          : 'Der var ingenting at kryptere om.',
      );
      setBekraefter(false);
      setNyNoegle(null);
      status.reload();
    });
  };

  return (
    <section className="card stack stack--tight">
      <h2>Krypteringsnøglen</h2>

      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}
      {fejl ? <Notice kind="error">{fejl}</Notice> : null}

      {tabte.length > 0 ? (
        /*
          Den værste tilstand, og den eneste, der ikke kan repareres herfra:
          nøglen er skiftet på serveren, uden at den gamle blev sat som
          ..._PREVIOUS. Så er den gemte API-nøgle tabt. Det skal stå her og
          nu — ellers viser det sig som en natlig hentning, der stille holdt
          op med at virke.
        */
        <Notice kind="error">
          <strong>Den gemte API-nøgle kan ikke læses.</strong>
          <p className="small mt-1">
            Krypteringsnøglen på serveren passer ikke til den, API-nøglen blev gemt med. Er
            den lige skiftet, så sæt den gamle tilbage som{' '}
            <code>CONVENTUS_CREDENTIAL_ENCRYPTION_KEY_PREVIOUS</code> — så kan den reddes
            herfra. Ellers skal foreningens Conventus-API-nøgle indtastes på ny ovenfor.
            Intet andet er gået tabt.
          </p>
        </Notice>
      ) : null}

      {igang ? (
        <>
          <Notice kind="warning">
            <strong>Rotationen er i gang.</strong>
            <p className="small mt-1">
              {antal(gamle.length, 'organisations API-nøgle', 'organisationers API-nøgler')} er
              stadig krypteret med den gamle nøgle. Serveren læser med den gamle og skriver med
              den nye — foreningens Conventus-API-nøgle skal ikke findes frem og indtastes igen.
            </p>
          </Notice>
          <p className="tiny muted">
            Fjern først <code>..._PREVIOUS</code> fra Railway <em>bagefter</em>. Gør du det før,
            er den gemte API-nøgle tabt og skal indtastes på ny.
          </p>
          {bekraefter ? (
            <div className="btn-group">
              <ActionButton className="btn" pending={action.pending} onClick={omkrypter}>
                Ja, kryptér om
              </ActionButton>
              <button type="button" className="btn btn--secondary" onClick={() => setBekraefter(false)}>
                Fortryd
              </button>
            </div>
          ) : (
            <p>
              <button type="button" className="btn" onClick={() => setBekraefter(true)}>
                Kryptér om med den nye nøgle
              </button>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="small muted">
            Den gemte Conventus-API-nøgle er krypteret med den nøgle, der står i Railway som{' '}
            <code>CONVENTUS_CREDENTIAL_ENCRYPTION_KEY</code>. Skal den skiftes, kan serveren
            kryptere om af sig selv — så skal API-nøglen ikke skaffes frem igen.
          </p>
          {aabn ? (
            <>
              <ol className="small" style={{ margin: '8px 0 0 18px', padding: 0 }}>
                <li>Lav en ny nøgle herunder. Den laves i din browser og sendes ingen steder hen.</li>
                <li>
                  Railway → <strong>skals-gf</strong> → Variables: sæt den{' '}
                  <strong>nuværende</strong> værdi af{' '}
                  <code>CONVENTUS_CREDENTIAL_ENCRYPTION_KEY</code> ind i en ny variabel{' '}
                  <code>CONVENTUS_CREDENTIAL_ENCRYPTION_KEY_PREVIOUS</code>, og sæt derefter
                  den nye nøgle ind i <code>CONVENTUS_CREDENTIAL_ENCRYPTION_KEY</code>. Udrul.
                </li>
                <li>Kom tilbage hertil. Så står knappen «Kryptér om» der.</li>
                <li>
                  Når den er trykket, slettes <code>..._PREVIOUS</code> fra Railway. Først
                  dér — ikke før.
                </li>
              </ol>

              {nyNoegle ? (
                <div className="card mt-3">
                  <p className="tiny muted">Ny CONVENTUS_CREDENTIAL_ENCRYPTION_KEY</p>
                  <p className="mono tiny" style={{ wordBreak: 'break-all', margin: '4px 0 8px' }}>
                    {nyNoegle}
                  </p>
                  <button type="button" className="btn btn--secondary btn--small" onClick={kopier}>
                    {kopieret ? 'Kopieret' : 'Kopiér'}
                  </button>
                  <p className="tiny muted mt-2">
                    Siden husker den ikke. Forlader du siden, laver du bare en ny — men har du
                    allerede skiftet den i Railway, skal det være den samme.
                  </p>
                </div>
              ) : (
                <p className="mt-3">
                  <button type="button" className="btn btn--secondary" onClick={generer}>
                    Lav en ny nøgle
                  </button>
                </p>
              )}
            </>
          ) : (
            <p>
              <button type="button" className="btn btn--ghost btn--small" onClick={() => setAabn(true)}>
                Skift krypteringsnøglen
              </button>
            </p>
          )}
        </>
      )}
    </section>
  );
}

export function ConventusPage(): JSX.Element {
  const state = useAsync<ConventusConfig>(() => api.get('/api/conventus/config'));
  const action = useAction();
  const [organisationId, setOrganisationId] = useState('');
  const [testGroupId, setTestGroupId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [previewGroupId, setPreviewGroupId] = useState('');
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [bulk, setBulk] = useState<BulkResult | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);

  const save = (): void => {
    void action.run(async () => {
      await api.put('/api/conventus/config', { organisationId, testGroupId, apiKey });
      action.setSuccess(
        apiKey.trim()
          ? 'Konfigurationen er gemt, og API-nøglen er krypteret.'
          : 'Konfigurationen er gemt. Den eksisterende API-nøgle er bevaret.',
      );
      setApiKey('');
      state.reload();
    });
  };

  const test = (): void => {
    void action.run(async () => {
      const result = await api.post<{ result: { ok: boolean; message: string } }>('/api/conventus/test');
      action.setSuccess(result.result.message);
      state.reload();
    });
  };

  const runPreview = (): void => {
    void action.run(async () => {
      const result = await api.get<{ preview: PreviewData }>(
        `/api/conventus/preview/${encodeURIComponent(previewGroupId)}`,
      );
      setPreview(result.preview);
    });
  };

  const importCatalog = (): void => {
    void action.run(async () => {
      const result = await api.post<{ catalog: { message: string } }>(
        '/api/conventus/import-catalog',
      );
      action.setSuccess(result.catalog.message);
      state.reload();
    });
  };

  const removeOrg = (id: string): void => {
    void action.run(async () => {
      const svar = await api.del<{ result: { removed: boolean; message: string } }>(
        `/api/conventus/organisations/${encodeURIComponent(id)}`,
      );
      action.setSuccess(svar.result.message);
      state.reload();
    });
  };

  const probeApi = (): void => {
    void action.run(async () => {
      const result = await api.post<ProbeResult>('/api/conventus/probe');
      setProbe(result);
      action.setSuccess(
        result.total > 0
          ? `${result.total} grupper i alt · ${result.withTimes} har en træningstid udfyldt.`
          : 'Ingen af kaldene gav en samlet holdliste. Se resultaterne herunder.',
      );
    });
  };

  const syncAll = (): void => {
    void action.run(async () => {
      const result = await api.post<{ bulk: BulkResult }>('/api/conventus/sync-all');
      setBulk(result.bulk);
      action.setSuccess(result.bulk.message);
      state.reload();
    });
  };

  const syncDirectory = (): void => {
    void action.run(async () => {
      const result = await api.post<{ directory: { message: string } }>('/api/conventus/directory-sync');
      action.setSuccess(result.directory.message);
      state.reload();
    });
  };

  return (
    <div className="stack">
      <PageHead
        title="Conventus"
        eyebrow="Integration"
        eyebrowIcon="conventus"
        sub="Appen læser fra Conventus og skriver aldrig tilbage"
      />
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state} skeletonLines={5}>
        {(data) => (
          <>
            <Notice kind="info">{data.endpointNote}</Notice>

            {data.organisationCount > 1 ? (
              <Notice kind="warning">
                Der er {data.organisationCount} organisationer gemt. Appen bruger den, der er
                gemt senest — {data.settings?.organisationId}. Står der en gammel eller opdigtet
                organisation fra en demo, bør den fjernes, så tallene ikke kan komme fra to
                steder.
                {/*
                  Fjernelsen rører KUN konfigurationen. Har organisationen
                  stadig hold, afvises den — så er der noget, der skal
                  flyttes først, og det er en beslutning, ikke en oprydning.
                */}
                <span className="row mt-2" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {(data.organisations ?? [])
                    .filter((id) => id !== data.settings?.organisationId)
                    .map((id) => (
                      <ActionButton
                        key={id}
                        className="btn btn--secondary btn--small"
                        pending={action.pending}
                        onClick={() => removeOrg(id)}
                      >
                        Fjern forening {id}
                      </ActionButton>
                    ))}
                </span>
              </Notice>
            ) : null}

            <KrypteringsnoegleCard />

            <section className="card stack stack--tight">
              <h2>Forbindelse</h2>
              {data.settings ? (
                <p className="small muted">
                  Organisation {data.settings.organisationId} ·{' '}
                  {data.settings.hasApiKey
                    ? `API-nøgle gemt ${data.settings.apiKeyUpdatedAt ? formatDateTime(data.settings.apiKeyUpdatedAt) : ''}`
                    : 'Ingen API-nøgle gemt endnu'}
                </p>
              ) : (
                <p className="small muted">Forbindelsen er ikke konfigureret endnu.</p>
              )}

              <Field label="Organisations-ID" htmlFor="cv-org">
                <input
                  id="cv-org"
                  type="text"
                  value={organisationId || data.settings?.organisationId || ''}
                  onChange={(e) => setOrganisationId((e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field label="Testgruppe-ID" htmlFor="cv-test">
                <input
                  id="cv-test"
                  type="text"
                  value={testGroupId || data.settings?.testGroupId || ''}
                  onChange={(e) => setTestGroupId((e.target as HTMLInputElement).value)}
                />
              </Field>
              <Field
                label="API-nøgle"
                htmlFor="cv-key"
                hint="Lad feltet stå tomt for at beholde den nøgle, der allerede er gemt. Nøglen vises aldrig igen."
              >
                <input
                  id="cv-key"
                  type="password"
                  autoComplete="off"
                  value={apiKey}
                  placeholder={data.settings?.hasApiKey ? '•••••••• (gemt)' : ''}
                  onChange={(e) => setApiKey((e.target as HTMLInputElement).value)}
                />
              </Field>
              <div className="btn-group">
                <ActionButton pending={action.pending} onClick={save}>
                  Gem
                </ActionButton>
                <ActionButton className="btn btn--secondary" pending={action.pending} onClick={test}>
                  Test forbindelse
                </ActionButton>
              </div>
              {data.settings?.lastTestAt ? (
                <p className="tiny muted">
                  Sidste test {formatDateTime(data.settings.lastTestAt)}:{' '}
                  {data.settings.lastTestMessage}
                </p>
              ) : null}
            </section>

            <section className="card">
              <h2>Medlemsfortegnelse</h2>
              {data.directory ? (
                <p className="small muted mt-2">
                  {data.directory.memberCount} personer ·{' '}
                  {data.directory.lastSuccessAt
                    ? `sidst hentet ${formatDateTime(data.directory.lastSuccessAt)}`
                    : 'endnu ikke hentet'}
                  <br />
                  {data.directory.message}
                </p>
              ) : (
                <p className="small muted mt-2">
                  Fortegnelsen er endnu ikke hentet.
                </p>
              )}
              <p className="mt-3">
                <ActionButton
                  className="btn btn--secondary"
                  pending={action.pending}
                  onClick={syncDirectory}
                >
                  Hent fortegnelsen nu
                </ActionButton>
              </p>
              <p className="tiny muted mt-2">
                Kun eksternt ID, navn og en mailhash gemmes. Mailhashen er en personrelateret
                oplysning — ikke anonymisering.
              </p>
            </section>

            {/*
              Automatikken skal kunne ses. En hentning, der fejler stille i
              tre uger, er værre end ingen automatik.
            */}
            <section className="section" aria-labelledby="automatik">
              <SectionHead title="Automatisk hentning" icon="clock" id="automatik" />
              <div className="card">
                {data.autoSync.enabled ? (
                  <div className="meta">
                    <Meta icon="clock" label="Interval">
                      Holdene hentes hver{' '}
                      {data.autoSync.intervalMinutes === 60
                        ? 'time'
                        : `${data.autoSync.intervalMinutes}. minut`}
                      , medlemsfortegnelsen hver{' '}
                      {data.autoSync.directoryHours === 24
                        ? 'nat'
                        : `${data.autoSync.directoryHours}. time`}
                      .
                    </Meta>
                    <Meta icon="teams" label="Sidste holdhentning">
                      {data.autoSync.teams
                        ? `${formatDateTime(data.autoSync.teams.at)} · ${
                            data.autoSync.teams.teams?.message ?? 'intet resultat'
                          }`
                        : 'Ikke kørt endnu'}
                    </Meta>
                    <Meta icon="family" label="Sidste fortegnelse">
                      {data.autoSync.directory
                        ? `${formatDateTime(data.autoSync.directory.at)} · ${
                            data.autoSync.directory.directory?.message ?? 'intet resultat'
                          }`
                        : 'Ikke kørt endnu'}
                    </Meta>
                  </div>
                ) : (
                  <p className="small muted">
                    Automatisk hentning er slået fra. Hold og medlemsfortegnelse opdateres kun,
                    når nogen trykker på knapperne herunder.
                  </p>
                )}
                {data.autoSync.teams?.error || data.autoSync.directory?.error ? (
                  <div className="mt-3">
                    <Notice kind="warning">
                      Sidste automatiske hentning meldte en fejl:{' '}
                      {data.autoSync.teams?.error ?? data.autoSync.directory?.error}
                    </Notice>
                  </div>
                ) : null}
                <p className="tiny muted mt-3">
                  Automatikken tvinger aldrig noget igennem: tomme lister godkendes ikke, og
                  hold med en uafklaret rolle springes over. Skal et af dem hentes alligevel,
                  kræver det en beslutning her på siden.
                </p>
              </div>
            </section>

            {/*
              Undersøgelsen findes, fordi appens holdkatalog vedligeholdes i
              hånden. Kan Conventus give hele listen ud fra foreningens ID,
              kan et nyt hold opdages af sig selv i stedet for at vente på,
              at nogen skriver det ind.
            */}
            <section className="card stack stack--tight">
              <h2>Undersøg API'et</h2>
              <p className="small muted">
                Prøver {data.probePathCount} kald, der kunne give hele foreningens
                holdliste ud fra foreningens ID alene. Der hentes ingen medlemmer, og
                der ændres ingenting.
              </p>
              <p className="tiny muted mt-2">
                Svaret herunder er kun <strong>strukturen</strong> — elementnavne, status og
                tal. Aldrig indhold. Et forkert gættet endepunkt kan svare med
                medlemsdata, og den slags skal ikke kunne havne i et skærmbillede.
              </p>
              <p className="mt-3">
                <ActionButton
                  className="btn btn--secondary"
                  pending={action.pending}
                  onClick={probeApi}
                >
                  Undersøg Conventus-API'et
                </ActionButton>
              </p>
              {probe && (probe.fill ?? []).length > 0 ? (
                <ul className="rows mt-3">
                  {(probe.fill ?? []).map((f) => (
                    <li key={f.field}>
                      <p className="small">
                        <span className="mono">{f.field}</span> — udfyldt på{' '}
                        <strong>{f.filled}</strong> af {probe.total} grupper
                        {f.hasChildren ? ' (feltet har underelementer)' : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : null}
              {probe && (probe.samples ?? []).length > 0 ? (
                <div className="mt-3">
                  <p className="small">
                    Sådan ser indholdet ud på de grupper, der har noget:
                  </p>
                  <ul className="rows mt-2">
                    {(probe.samples ?? []).map((s) => (
                      <li key={s.groupId}>
                        <p className="small">{s.title}</p>
                        <p className="tiny mono">{s.trainingTimes}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {probe ? (
                <ul className="rows mt-3">
                  {(probe.attempts ?? []).map((a) => (
                    <li key={a.label} className="stack stack--tight">
                      <p className="small">
                        <strong>{a.label}</strong>
                        {' — '}
                        {a.failure
                          ? a.failure
                          : a.remoteError
                            ? `Conventus svarede: ${a.remoteError}`
                            : a.groupCount > 1
                              ? `${a.groupCount} grupper i svaret`
                              : a.groupCount === 1
                                ? 'Én gruppe i svaret'
                                : 'Ingen grupper i svaret'}
                      </p>
                      <p className="tiny muted">
                        HTTP {a.status ?? '–'} · {a.bytes} tegn
                      </p>
                      {a.trainingTimesShape ? (
                        <p className="small">
                          <strong>traeningstider indeholdt noget:</strong>{' '}
                          <span className="mono">{a.trainingTimesShape}</span>
                        </p>
                      ) : null}
                      {(a.fields ?? []).length > 0 ? (
                        <p className="tiny mono">
                          <strong>Felter:</strong> {(a.fields ?? []).join(', ')}
                        </p>
                      ) : null}
                      {a.shape ? <p className="tiny muted mono">{a.shape}</p> : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>

            <section className="card stack stack--tight">
              <h2>Hent alle hold</h2>
              <p className="small muted">
                Er holdene ikke tilknyttet foreningen endnu — eller blev de oprettet
                under en demo — så tilknyt holdkataloget først. Det flytter katalogets
                {' '}{data.catalogSize} hold og medlemskaber over på den forening, der står
                ovenfor, uden at oprette dem forfra.
              </p>
              <p className="mt-2">
                <ActionButton
                  className="btn btn--secondary"
                  pending={action.pending}
                  onClick={importCatalog}
                >
                  Tilknyt holdkataloget til foreningen
                </ActionButton>
              </p>
              <p className="small muted mt-4">
                Henter medlemslisten for samtlige tilknyttede hold. Hold, hvor en rolle
                ikke kan fortolkes, springes over og vises herunder — de kræver en
                beslutning og bliver ikke tvunget igennem. Svarer Conventus med en tom
                liste for et hold, bevares den seneste kendte liste.
              </p>
              <p className="mt-3">
                <ActionButton className="btn" pending={action.pending} onClick={syncAll}>
                  Hent alle hold fra Conventus
                </ActionButton>
              </p>
              {bulk ? (
                <div className="stack stack--tight mt-3">
                  <p className="small">
                    <strong>{bulk.ok}</strong> hold hentet
                    {bulk.blocked > 0 ? ` · ${bulk.blocked} sprunget over` : ''}
                    {bulk.failed > 0 ? ` · ${bulk.failed} fejlede` : ''}
                  </p>
                  {/* Kun det, der kræver opmærksomhed. 45 grønne linjer hjælper ingen. */}
                  {bulk.results.filter((r) => r.status !== 'ok').length > 0 ? (
                    <ul className="small mt-1">
                      {bulk.results
                        .filter((r) => r.status !== 'ok')
                        .map((r) => (
                          <li key={r.groupId}>
                            <strong>{r.name}</strong> — {r.message}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="small muted">Alle hold blev hentet uden problemer.</p>
                  )}
                </div>
              ) : null}
            </section>

            <section className="card stack stack--tight">
              <h2>Preview og import</h2>
              <p className="small muted">
                Preview henter gruppen og viser, hvad en import ville give.{' '}
                <strong>Det ændrer ikke en eksisterende deltagerliste.</strong>
              </p>
              <Field label="Gruppe-ID" htmlFor="cv-preview">
                <input
                  id="cv-preview"
                  type="text"
                  value={previewGroupId}
                  onChange={(e) => setPreviewGroupId((e.target as HTMLInputElement).value)}
                />
              </Field>
              <ActionButton
                className="btn btn--secondary"
                pending={action.pending}
                onClick={runPreview}
                disabled={!previewGroupId.trim()}
              >
                Vis preview
              </ActionButton>

              {preview ? <PreviewResult preview={preview} onSynced={state.reload} /> : null}
            </section>
          </>
        )}
      </AsyncSection>
    </div>
  );
}

interface PreviewData {
  groupId: string;
  groupName: string;
  capacity: number | null;
  waitlistCount: number | null;
  capacityNote: string;
  members: Array<{
    memberId: string;
    name: string;
    teamRole: string;
    sourceRole: string;
    warning?: string;
    overridden: boolean;
  }>;
  summary: { participants: number; trainers: number; helpers: number; unresolved: number };
  warnings: string[];
  blockedReason: string | null;
  linkedTeamId: string | null;
}

const TEAM_ROLE_LABELS: Record<string, string> = {
  participant: 'Deltager',
  trainer: 'Instruktør',
  helper: 'Hjælper',
  unresolved: 'Uafklaret',
};

function PreviewResult({
  preview,
  onSynced,
}: {
  preview: PreviewData;
  onSynced: () => void;
}): JSX.Element {
  const action = useAction();
  const [allowEmpty, setAllowEmpty] = useState(false);
  const [allowUnresolved, setAllowUnresolved] = useState(false);

  const sync = (): void => {
    void action.run(async () => {
      const result = await api.post<{ result: { status: string; message: string } }>(
        '/api/conventus/sync',
        { groupId: preview.groupId, allowEmpty, allowUnresolved },
      );
      action.setSuccess(`${result.result.status.toUpperCase()}: ${result.result.message}`);
      onSynced();
    });
  };

  const setOverride = (memberId: string, teamRole: string): void => {
    void action.run(async () => {
      await api.post('/api/conventus/roles', { groupId: preview.groupId, memberId, teamRole });
      action.setSuccess('Rollen er rettet lokalt. Conventus er ikke ændret.');
    });
  };

  return (
    <div className="card" style={{ background: 'var(--bg)', marginTop: 12 }}>
      <h3>{preview.groupName}</h3>
      <p className="tiny muted">
        Gruppe {preview.groupId}
        {preview.linkedTeamId ? ` · allerede tilknyttet ${preview.linkedTeamId}` : ' · ikke tilknyttet endnu'}
      </p>

      {preview.capacity !== null ? (
        <p className="tiny muted mt-2">
          Kapacitet {preview.capacity}
          {preview.waitlistCount !== null ? ` · venteliste ${preview.waitlistCount}` : ''}.{' '}
          {preview.capacityNote}
        </p>
      ) : null}

      <div className="row" style={{ gap: 6, marginTop: 10 }}>
        <Badge>{antal(preview.summary.participants, 'deltager', 'deltagere')}</Badge>
        <Badge>{antal(preview.summary.trainers, 'instruktør', 'instruktører')}</Badge>
        <Badge>{preview.summary.helpers} hjælpere</Badge>
        {preview.summary.unresolved > 0 ? (
          <Badge kind="warning">{preview.summary.unresolved} uafklarede</Badge>
        ) : null}
      </div>

      {preview.warnings.map((w, i) => (
        <div key={i} className="mt-2">
          <Notice kind="warning">{w}</Notice>
        </div>
      ))}

      {action.error ? (
        <div className="mt-2">
          <Notice kind="error">{action.error}</Notice>
        </div>
      ) : null}
      {action.success ? (
        <div className="mt-2">
          <Notice kind="success">{action.success}</Notice>
        </div>
      ) : null}

      {preview.members.length > 0 ? (
        <div className="table-scroll mt-3">
          <table className="table-cards">
            <thead>
              <tr>
                <th scope="col">Navn</th>
                <th scope="col">Rolle i Conventus</th>
                <th scope="col">Fortolket som</th>
                <th scope="col">Ret</th>
              </tr>
            </thead>
            <tbody>
              {preview.members.map((m) => (
                <tr key={m.memberId}>
                  <td data-label="Navn">{m.name}</td>
                  <td className="muted" data-label="Rolle i Conventus">
                    {m.sourceRole || '(ingen rolle angivet)'}
                  </td>
                  <td data-label="Fortolket som">
                    <Badge kind={m.teamRole === 'unresolved' ? 'warning' : undefined}>
                      {TEAM_ROLE_LABELS[m.teamRole]}
                    </Badge>
                    {m.overridden ? <span className="tiny muted"> (rettet lokalt)</span> : null}
                  </td>
                  <td data-label="Ret">
                    <select
                      aria-label={`Ret rolle for ${m.name}`}
                      value={m.teamRole}
                      onChange={(e) => setOverride(m.memberId, (e.target as HTMLSelectElement).value)}
                    >
                      {Object.entries(TEAM_ROLE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="small muted mt-3">
          Gruppen har ingen medlemmer i Conventus lige nu.
        </p>
      )}

      <div className="stack stack--tight mt-4">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={allowEmpty}
            onChange={(e) => setAllowEmpty((e.target as HTMLInputElement).checked)}
          />
          <span className="small">
            Godkend en tom deltagerliste
            <span className="tiny muted" style={{ display: 'block' }}>
              Nødvendigt for hold, der reelt er tomme — fx et hold der afventer instruktør. Uden dette
              bevares den seneste kendte liste.
            </span>
          </span>
        </label>
        {preview.blockedReason ? (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={allowUnresolved}
              onChange={(e) => setAllowUnresolved((e.target as HTMLInputElement).checked)}
            />
            <span className="small">
              Synkronisér alligevel med uafklarede roller
              <span className="tiny muted" style={{ display: 'block' }}>
                {preview.blockedReason}
              </span>
            </span>
          </label>
        ) : null}
        <ActionButton pending={action.pending} onClick={sync}>
          Synkronisér gruppen
        </ActionButton>
      </div>
    </div>
  );
}
